import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildProductPath } from '../utils/productSlug';
import { collection, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { StoreContext } from '../contexts/StoreContext';
import { navItems } from '../constants/navItems';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS } from '../constants/layout';
import { COLORS } from '../constants/colors';
import { db } from '../firebase';

const FALLBACK_LOGO_URL =
  'https://firebasestorage.googleapis.com/v0/b/dnd-seafood.firebasestorage.app/o/dndlogo.png?alt=media&token=b130d4ca-15fb-4660-a42d-d79feec1e1b8';

function NavigationBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { store, loading } = useContext(StoreContext);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  // Load cached navigation bar preferences from localStorage
  const getCachedPreferences = () => {
    try {
      const cached = localStorage.getItem('navigationBarPreferences');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error loading cached preferences:', error);
    }
    return { logoURL: null, logoSize: 40, navBarStyle: 'modern' };
  };

  const cachedPrefs = getCachedPreferences();
  const [logoURL, setLogoURL] = useState(cachedPrefs.logoURL);
  const [logoSize, setLogoSize] = useState(cachedPrefs.logoSize);
  const [navBarStyle, setNavBarStyle] = useState(cachedPrefs.navBarStyle || 'modern');
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [desktopSearchTerm, setDesktopSearchTerm] = useState('');
  const [isDesktopSearchFocused, setIsDesktopSearchFocused] = useState(false);
  const searchInputRef = useRef(null);
  const desktopSearchRef = useRef(null);

  const showModernDesktop = !isMobile && navBarStyle === 'modern';
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        window.innerWidth <= 768 ||
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  // Helper to see if a given path is "active"
  const isActive = (path) => {
    if (path === '/') {
      return (
        location.pathname === '/' ||
        location.pathname.startsWith('/product/')
      );
    }
    return location.pathname === path;
  };

  const linkStyle = (path, overrides = {}) => {
    const active = isActive(path);
    return {
      textDecoration: 'none',
      color: active
        ? 'var(--color-navbar-pill-text)'
        : 'var(--color-navbar-text)',
      fontWeight: '600',
      padding: '6px 12px',
      borderRadius: BORDER_RADIUS,
      backgroundColor: active
        ? 'var(--color-navbar-pill-bg)'
        : 'transparent',
      transition: 'all 0.2s ease',
      display: 'block',
      ...overrides,
    };
  };

  const iconButtonStyle = {
    border: '1px solid var(--color-navbar-border)',
    backgroundColor: isSearchHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isSearchHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
    flexShrink: 0,
  };

  const trimmedSearch = searchTerm.trim();

  const filteredProducts = useMemo(() => {
    const term = trimmedSearch.toLowerCase();
    if (!term) {
      return [];
    }

    return products
      .filter(product => {
        const name = (product.productName || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        return name.includes(term) || description.includes(term);
      })
      .sort((a, b) => {
        const nameA = (a.productName || '').toLowerCase();
        const nameB = (b.productName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [products, trimmedSearch]);

  const searchResults = filteredProducts.slice(0, 20);
  const hasQuery = trimmedSearch.length > 0;
  const showNoResults = hasQuery && searchResults.length === 0;
  const showResultsSummary = hasQuery && !showNoResults;

  const handleSearchOpen = () => {
    setIsSearchHovered(false);
    setSearchTerm('');
    setIsSearchOpen(true);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchTerm('');
    setIsSearchHovered(false);
  };

  const handleSearchSubmit = event => {
    event.preventDefault();
    if (searchResults.length === 0) {
      return;
    }
    const firstResult = searchResults[0];
    navigate(buildProductPath(firstResult));
    handleSearchClose();
  };

  useEffect(() => {
    if (!isSearchOpen) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) {
      return undefined;
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsSearchOpen(false);
        setSearchTerm('');
        setIsSearchHovered(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchOpen]);

  // Only show the store name once it's loaded and available
  const storeTitle = !loading && store?.name ? store.name : '';
  const brandLinkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    textDecoration: 'none',
  };
  const storeTitleStyle = {
    color: COLORS.brandRed,
    fontSize: '20px',
    fontFamily: "'Caprasimo', Georgia, serif",
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '-0.02em',
  };
  const logoStyle = {
    height: `${logoSize}px`,
    width: `${logoSize}px`,
    minHeight: `${logoSize}px`,
    minWidth: `${logoSize}px`,
    objectFit: 'contain',
    opacity: logoLoaded ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out',
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchCategories() {
      try {
        const snap = await getDocs(collection(db, 'categories'));
        if (!isMounted) return;
        const fetched = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cat => !!(cat?.title || cat?.name))
          .sort((a, b) => {
            const titleA = (a.title || a.name || '').toLowerCase();
            const titleB = (b.title || b.name || '').toLowerCase();
            return titleA.localeCompare(titleB);
          });
        setCategories(fetched);
      } catch (error) {
        if (isMounted) {
          setCategories([]);
        }
      }
    }

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchProducts() {
      try {
        const snap = await getDocs(collection(db, 'products'));
        if (!isMounted) return;
        const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(fetched);
      } catch (error) {
        if (isMounted) {
          setProducts([]);
        }
      }
    }

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  // Listen to navigation bar preferences for dynamic logo and style
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'navigation_bar_preferences', 'default'),
      (docSnapshot) => {
        let newLogoURL = null;
        let newLogoSize = 40;
        let newNavBarStyle = 'modern';

        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          newLogoURL = data?.logo_url || null;

          const sizeMap = { small: 64, default: 80, large: 96 };
          const size = data?.logo_size || 'default';
          newLogoSize = sizeMap[size] || 40;

          newNavBarStyle = data?.nav_bar_style || 'modern';

          // Dynamic favicon
          const faviconURL = data?.favicon_url;
          if (faviconURL) {
            let link = document.querySelector('link[rel="icon"]');
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = faviconURL;
          }
        }

        setLogoURL(newLogoURL);
        setLogoSize(newLogoSize);
        setNavBarStyle(newNavBarStyle);

        try {
          localStorage.setItem('navigationBarPreferences', JSON.stringify({
            logoURL: newLogoURL,
            logoSize: newLogoSize,
            navBarStyle: newNavBarStyle,
          }));
        } catch (error) {
          console.error('Error caching preferences:', error);
        }
      },
      (error) => {
        console.error('Error fetching navigation preferences:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Reset logo loaded state when URL changes
  useEffect(() => {
    setLogoLoaded(false);
  }, [logoURL]);

  const trimmedDesktopSearch = desktopSearchTerm.trim();

  const desktopSearchResults = useMemo(() => {
    const term = trimmedDesktopSearch.toLowerCase();
    if (!term) return [];
    return products
      .filter(product => {
        const name = (product.productName || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        return name.includes(term) || description.includes(term);
      })
      .sort((a, b) => (a.productName || '').toLowerCase().localeCompare((b.productName || '').toLowerCase()))
      .slice(0, 8);
  }, [products, trimmedDesktopSearch]);

  const headerNavItems = navItems.filter(item => item.path !== '/');

  const sidebarLinkStyle = (path) => {
    const active = isActive(path);
    return {
      textDecoration: 'none',
      color: active ? 'var(--color-navbar-pill-text)' : 'var(--color-navbar-text)',
      fontWeight: active ? '600' : 'normal',
      padding: '10px 12px',
      borderRadius: active ? BORDER_RADIUS : 0,
      backgroundColor: active ? 'var(--color-navbar-pill-bg)' : 'transparent',
      transition: 'all 0.2s ease',
      display: 'block',
      width: '100%',
      boxSizing: 'border-box',
    };
  };

  const SidebarDivider = () => (
    <div style={{ margin: '0 12px', borderBottom: '1px solid var(--color-border)' }} />
  );

  const sectionHeaderStyle = {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '10px',
    paddingLeft: '12px',
    color: 'var(--color-navbar-text)',
    opacity: 0.7,
  };

  const iconStyle = {
    fontSize: '20px',
    width: '24px',
    textAlign: 'center',
    flexShrink: 0,
  };

  const SidebarLabel = ({ icon, label }) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {icon && <span className="material-icons" style={iconStyle}>{icon}</span>}
      {label}
    </span>
  );

  const sidebarMenuContent = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {headerNavItems.map((item, i) => (
          <React.Fragment key={item.path}>
            <Link to={item.path} className="sidebar-link" style={sidebarLinkStyle(item.path)}>
              <SidebarLabel icon={item.icon} label={item.label} />
            </Link>
            {i < headerNavItems.length - 1 && <SidebarDivider />}
          </React.Fragment>
        ))}
      </div>

      {categories.length > 0 && (
        <div style={{ paddingTop: '12px', display: 'flex', flexDirection: 'column' }}>
          <span style={sectionHeaderStyle}>Categories</span>
          {categories.map((category, i) => {
            const label = category.title || category.name || 'Category';
            return (
              <React.Fragment key={category.id}>
                <Link to={`/category/${category.id}`} className="sidebar-link" style={sidebarLinkStyle(`/category/${category.id}`)}>
                  {label}
                </Link>
                {i < categories.length - 1 && <SidebarDivider />}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </>
  );

  const menuButtonStyle = {
    border: 'none',
    backgroundColor: isMenuHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isMenuHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
    padding: 0,
  };

  const searchButtonStyle = {
    border: 'none',
    backgroundColor: isSearchHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isSearchHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const searchOverlay = !showModernDesktop && isSearchOpen && portalTarget
    ? createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search products"
      onClick={handleSearchClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 15, 15, 0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '80px 16px 80px',
        boxSizing: 'border-box',
        minHeight: '100vh',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '640px',
          padding: '24px',
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.25)',
          border: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          maxHeight: 'calc(100vh - 160px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}
        >
          <form
            onSubmit={handleSearchSubmit}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <label
              htmlFor="nav-search-input"
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--color-text-secondary)',
              }}
            >
              Search our catalog
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <input
                id="nav-search-input"
                ref={searchInputRef}
                type="search"
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Search for seafood, party trays, and more"
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-surface-muted)',
                  color: 'var(--color-text-primary)',
                  fontSize: '1rem',
                  outline: 'none',
                  boxShadow: '0 0 0 2px transparent',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                }}
                onFocus={event => {
                  event.target.style.boxShadow =
                    '0 0 0 3px rgba(185, 28, 28, 0.25)';
                  event.target.style.borderColor = 'var(--color-accent)';
                }}
                onBlur={event => {
                  event.target.style.boxShadow = '0 0 0 2px transparent';
                  event.target.style.borderColor = 'var(--color-border)';
                }}
              />
              <button
                type="submit"
                style={{
                  border: 'none',
                  backgroundColor: 'var(--color-accent)',
                  color: 'var(--color-accent-text)',
                  padding: '12px 20px',
                  borderRadius: '9999px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease, transform 0.2s ease',
                  boxShadow: '0 10px 20px rgba(185, 28, 28, 0.25)',
                  whiteSpace: 'nowrap',
                }}
                onMouseDown={event => event.preventDefault()}
                onMouseEnter={event => {
                  event.currentTarget.style.backgroundColor =
                    'var(--color-accent-hover)';
                  event.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={event => {
                  event.currentTarget.style.backgroundColor =
                    'var(--color-accent)';
                  event.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Search
              </button>
            </div>
          </form>
          <button
            type="button"
            onClick={handleSearchClose}
            aria-label="Close search dialog"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              borderRadius: '9999px',
              width: '36px',
              height: '36px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease, color 0.2s ease',
            }}
            onMouseEnter={event => {
              event.currentTarget.style.backgroundColor =
                'var(--color-surface-muted)';
              event.currentTarget.style.color = 'var(--color-navbar-text)';
            }}
            onMouseLeave={event => {
              event.currentTarget.style.backgroundColor = 'transparent';
              event.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
          >
            <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>×</span>
          </button>
        </div>
        <div
          style={{
            fontSize: '0.9rem',
            color: 'var(--color-text-secondary)',
          }}
        >
          {hasQuery && (
            <>
              {showNoResults
                ? `No products found for "${trimmedSearch}".`
                : showResultsSummary &&
                  `Showing ${searchResults.length} matching ${
                    searchResults.length === 1 ? 'item' : 'items'
                  }.`}
            </>
          )}
        </div>
        {searchResults.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              overflowY: 'auto',
              paddingRight: '4px',
            }}
          >
            {searchResults.map(product => {
              const name = product.productName || 'View product';
              const rawDescription = (product.description || '').trim();
              const snippet =
                rawDescription.length > 140
                  ? `${rawDescription.slice(0, 137)}…`
                  : rawDescription;

              return (
                <Link
                  key={product.id}
                  to={buildProductPath(product)}
                  onClick={handleSearchClose}
                  style={{
                    textDecoration: 'none',
                    border: '1px solid var(--color-border)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    backgroundColor: 'var(--color-surface-muted)',
                    color: 'var(--color-text-primary)',
                    transition:
                      'background-color 0.2s ease, border-color 0.2s ease',
                    display: 'block',
                  }}
                  onMouseEnter={event => {
                    event.currentTarget.style.backgroundColor =
                      'var(--color-card-background-hover)';
                    event.currentTarget.style.borderColor =
                      'var(--color-accent)';
                  }}
                  onMouseLeave={event => {
                    event.currentTarget.style.backgroundColor =
                      'var(--color-surface-muted)';
                    event.currentTarget.style.borderColor =
                      'var(--color-border)';
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      display: 'block',
                      marginBottom: snippet ? '4px' : 0,
                    }}
                  >
                    {name}
                  </span>
                  {snippet && (
                    <span
                      style={{
                        fontSize: '0.875rem',
                        color: 'var(--color-text-secondary)',
                        display: 'block',
                      }}
                    >
                      {snippet}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    portalTarget
  ) : null;

  const iconSvgStyle = {
    width: '18px',
    height: '18px',
    display: 'block',
  };

  // ─── Modern Desktop Nav ───────────────────────────────────────────────────
  if (showModernDesktop) {
    const showDesktopDropdown = isDesktopSearchFocused && trimmedDesktopSearch.length > 0;

    return (
      <>
        <nav
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'var(--color-navbar-surface)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 100,
            borderBottom: '1px solid var(--color-navbar-border)',
          }}
        >
          {/* Row 1: Logo | Search bar */}
          <div
            style={{
              maxWidth: MAX_CONTENT_WIDTH,
              margin: '0 auto',
              padding: '0 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              height: `${logoSize + 20}px`,
            }}
          >
            {/* Logo + Store Name */}
            <Link to="/" style={brandLinkStyle}>
              {(logoURL || FALLBACK_LOGO_URL) && (
                <img
                  src={logoURL || FALLBACK_LOGO_URL}
                  alt="D&D Seafood logo"
                  style={{
                    height: `${logoSize}px`,
                    minWidth: `${logoSize}px`,
                    width: 'auto',
                    maxWidth: '160px',
                    objectFit: 'contain',
                    opacity: logoLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                  onLoad={() => setLogoLoaded(true)}
                />
              )}
              {storeTitle && (
                <span style={storeTitleStyle}>{storeTitle}</span>
              )}
            </Link>

            {/* Inline search bar */}
            <div style={{ flex: 1, position: 'relative' }}>
              <form
                onSubmit={event => {
                  event.preventDefault();
                  if (desktopSearchResults.length > 0) {
                    navigate(buildProductPath(desktopSearchResults[0]));
                    setDesktopSearchTerm('');
                    setIsDesktopSearchFocused(false);
                  }
                }}
                style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: '14px',
                    color: 'var(--color-text-secondary)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }} aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  ref={desktopSearchRef}
                  type="search"
                  value={desktopSearchTerm}
                  onChange={event => setDesktopSearchTerm(event.target.value)}
                  onFocus={() => setIsDesktopSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsDesktopSearchFocused(false), 150)}
                  placeholder="Search products..."
                  style={{
                    width: '100%',
                    padding: '9px 14px 9px 40px',
                    borderRadius: BORDER_RADIUS,
                    border: '1.5px solid var(--color-border)',
                    backgroundColor: 'var(--color-surface-muted)',
                    color: 'var(--color-text-primary)',
                    fontSize: '0.85rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  onFocusCapture={event => {
                    event.target.style.borderColor = 'var(--color-accent)';
                    event.target.style.boxShadow = '0 0 0 3px rgba(185, 28, 28, 0.25)';
                  }}
                  onBlurCapture={event => {
                    event.target.style.borderColor = 'var(--color-border)';
                    event.target.style.boxShadow = 'none';
                  }}
                />
              </form>

              {/* Inline search dropdown */}
              {showDesktopDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '12px',
                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
                    zIndex: 200,
                    overflow: 'hidden',
                  }}
                >
                  {desktopSearchResults.length === 0 ? (
                    <div style={{ padding: '16px', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                      No products found for &ldquo;{trimmedDesktopSearch}&rdquo;
                    </div>
                  ) : (
                    desktopSearchResults.map(product => {
                      const name = product.productName || 'View product';
                      return (
                        <Link
                          key={product.id}
                          to={buildProductPath(product)}
                          onClick={() => {
                            setDesktopSearchTerm('');
                            setIsDesktopSearchFocused(false);
                          }}
                          style={{
                            textDecoration: 'none',
                            padding: '12px 16px',
                            display: 'block',
                            color: 'var(--color-text-primary)',
                            borderBottom: '1px solid var(--color-border)',
                            transition: 'background-color 0.15s ease',
                          }}
                          onMouseEnter={event => {
                            event.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                          }}
                          onMouseLeave={event => {
                            event.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{name}</span>
                        </Link>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Category strip */}
          <div
            style={{
              borderTop: '1px solid var(--color-navbar-border)',
              backgroundColor: 'var(--color-navbar-surface)',
              display: 'block',
            }}
          >
            <div
              style={{
                maxWidth: MAX_CONTENT_WIDTH,
                margin: '0 auto',
                padding: '0 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: '40px',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {headerNavItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    textDecoration: 'none',
                    color: isActive(item.path) ? 'var(--color-navbar-pill-text)' : 'var(--color-navbar-text)',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    padding: '4px 12px',
                    borderRadius: BORDER_RADIUS,
                    backgroundColor: isActive(item.path) ? 'var(--color-navbar-pill-bg)' : 'transparent',
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                  }}
                  onMouseEnter={e => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {item.label}
                </Link>
              ))}

              {categories.length > 0 && (
                <>
                  <span
                    style={{
                      width: '1px',
                      height: '16px',
                      backgroundColor: 'var(--color-navbar-border)',
                      margin: '0 4px',
                      flexShrink: 0,
                    }}
                  />
                  {categories.map(category => {
                    const label = category.title || category.name || 'Category';
                    const catPath = `/category/${category.id}`;
                    return (
                      <Link
                        key={category.id}
                        to={catPath}
                        style={{
                          textDecoration: 'none',
                          color: isActive(catPath) ? 'var(--color-navbar-pill-text)' : 'var(--color-navbar-text)',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          padding: '4px 12px',
                          borderRadius: BORDER_RADIUS,
                          backgroundColor: isActive(catPath) ? 'var(--color-navbar-pill-bg)' : 'transparent',
                          transition: 'all 0.15s ease',
                          flexShrink: 0,
                        }}
                        onMouseEnter={e => {
                          if (!isActive(catPath)) {
                            e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                          }
                        }}
                        onMouseLeave={e => {
                          if (!isActive(catPath)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {label}
                      </Link>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </nav>

        <style jsx>{`
          div::-webkit-scrollbar { display: none; }
        `}</style>
      </>
    );
  }

  if (isMobile) {
    return (
      <>
        {/* Mobile Navigation */}
        <nav
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'var(--color-navbar-surface)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 100,
            borderBottom: '1px solid var(--color-navbar-border)',
          }}
        >
          <div
            className="nav-safe-area"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
            }}
          >
            <Link
              to="/"
              style={brandLinkStyle}
            >
              <img src={logoURL || FALLBACK_LOGO_URL} alt="D&D Seafood logo" style={logoStyle} onLoad={() => setLogoLoaded(true)} />
              {storeTitle && (
                <span style={storeTitleStyle}>{storeTitle}</span>
              )}
            </Link>

            {/* Hamburger Menu Button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <button
                type="button"
                onClick={handleSearchOpen}
                style={iconButtonStyle}
                aria-label="Open product search"
                onMouseEnter={() => setIsSearchHovered(true)}
                onMouseLeave={() => setIsSearchHovered(false)}
                onFocus={() => setIsSearchHovered(true)}
                onBlur={() => setIsSearchHovered(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={iconSvgStyle}
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-around',
                  width: '24px',
                  height: '24px',
                  position: 'relative',
                }}
                aria-label="Toggle menu"
              >
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'var(--color-navbar-icon)',
                    borderRadius: '1px',
                    transition: 'all 0.3s ease',
                    transform: isMenuOpen
                      ? 'rotate(45deg) translate(5px, 5px)'
                      : 'none',
                  }}
                />
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'var(--color-navbar-icon)',
                    borderRadius: '1px',
                    transition: 'all 0.3s ease',
                    opacity: isMenuOpen ? 0 : 1,
                  }}
                />
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'var(--color-navbar-icon)',
                    borderRadius: '1px',
                    transition: 'all 0.3s ease',
                    transform: isMenuOpen
                      ? 'rotate(-45deg) translate(7px, -6px)'
                      : 'none',
                  }}
                />
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99,
              animation: 'fadeIn 0.2s ease',
            }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Mobile Menu Panel */}
        <div
          className="sidebar-safe-area-right"
          style={{
            position: 'fixed',
            top: 0,
            right: isMenuOpen ? 0 : '-280px',
            width: '280px',
            height: '100vh',
            backgroundColor: 'var(--color-mobile-menu-surface)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
            zIndex: 101,
            transition: 'right 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: `${logoSize + 20}px`,
          }}
        >
          <div
            style={{
              padding: '12px',
              overflowY: 'auto',
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {sidebarMenuContent}
          </div>
        </div>

        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .sidebar-link:hover {
            background-color: rgba(128, 128, 128, 0.15) !important;
          }
        `}</style>
        {searchOverlay}
      </>
    );
  }

  // ─── Desktop Classic Nav ────────────────────────────────────────────────
  return (
    <>
      <nav
        style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: 'var(--color-navbar-surface)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 100,
          borderBottom: '1px solid var(--color-navbar-border)',
        }}
      >
        <div
          className="nav-safe-area"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 16px',
            maxWidth: MAX_CONTENT_WIDTH,
            margin: '0 auto',
            width: '100%',
          }}
        >
          {/* Hamburger Menu Button - Left */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={menuButtonStyle}
            aria-label="Toggle menu"
            onMouseEnter={() => setIsMenuHovered(true)}
            onMouseLeave={() => setIsMenuHovered(false)}
            onFocus={() => setIsMenuHovered(true)}
            onBlur={() => setIsMenuHovered(false)}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                width: '18px',
                height: '18px',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(45deg) translate(4px, 4px)'
                    : 'none',
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  opacity: isMenuOpen ? 0 : 1,
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(-45deg) translate(5px, -5px)'
                    : 'none',
                }}
              />
            </div>
          </button>

          {/* Logo - Center */}
          <Link
            to="/"
            style={{
              ...brandLinkStyle,
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <img
              src={logoURL || FALLBACK_LOGO_URL}
              alt="D&D Seafood logo"
              style={logoStyle}
              onLoad={() => setLogoLoaded(true)}
            />
            {storeTitle && (
              <span style={storeTitleStyle}>{storeTitle}</span>
            )}
          </Link>

          {/* Search Button - Right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={handleSearchOpen}
              style={searchButtonStyle}
              aria-label="Open product search"
              onMouseEnter={() => setIsSearchHovered(true)}
              onMouseLeave={() => setIsSearchHovered(false)}
              onFocus={() => setIsSearchHovered(true)}
              onBlur={() => setIsSearchHovered(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={iconSvgStyle}
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Menu Overlay */}
      {isMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99,
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Sidebar Menu Panel */}
      <div
        className="sidebar-safe-area-right"
        style={{
          position: 'fixed',
          top: 0,
          right: isMenuOpen ? 0 : '-320px',
          width: '320px',
          height: '100vh',
          backgroundColor: 'var(--color-mobile-menu-surface)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
          zIndex: 101,
          transition: 'right 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: `${logoSize + 20}px`,
        }}
      >
        <div
          style={{
            padding: '12px',
            overflowY: 'auto',
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {sidebarMenuContent}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .sidebar-link:hover {
          background-color: rgba(128, 128, 128, 0.15) !important;
        }
      `}</style>
      {searchOverlay}
    </>
  );
}

export default NavigationBar;
